import { Shell } from "@/components/Shell";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@/components/ui/input-group";
import { authClient } from "@/lib/auth-client";
import { useMutation } from "@tanstack/react-query";
import { EyeIcon, EyeOffIcon, LockIcon, MailIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

const schema = z.object({
	email: z.email("Invalid email address"),
	password: z.string("Password is required").min(1, "Password is required"),
});

export function SignIn() {
	const [showPassword, setShowPassword] = useState(false);
	const { data: session } = authClient.useSession();

	const { mutate: signIn, isPending } = useMutation({
		mutationFn: async ({
			email,
			password,
		}: {
			email: string;
			password: string;
		}) => {
			const result = await authClient.signIn.email({
				email,
				password,
				callbackURL: "/",
			});
			if (result.error) {
				if (result.error.code === "EMAIL_NOT_VERIFIED") {
					toast.error(result.error.message, {
						action: {
							label: "Resend verification email",
							onClick: () => authClient.sendVerificationEmail({ email }),
						},
					});
				} else {
					toast.error(result.error.message ?? "Sign in failed");
				}
			}

			return result.data;
		},
	});

	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: { email: "", password: "" },
		onSubmit: async ({ value }) => {
			signIn({
				email: value.email,
				password: value.password,
			});
		},
	});

	if (session) {
		return <Navigate to="/" replace />;
	}

	return (
		<Shell className="flex items-center justify-center h-dvh">
			<BackgroundBeams />
			<Card className="w-full max-w-md z-1 bg-card/20">
				<CardHeader>
					<CardTitle>Sign in</CardTitle>
					<CardDescription>Use your email and password</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						id="sign-in"
					>
						<FieldGroup>
							<FieldSet>
								<form.Field name="email">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="email">Email</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<MailIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="email"
													type="email"
													placeholder="example@example.com"
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													autoComplete="off"
													disabled={isPending}
													className="h-8"
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="password">
									{(field) => (
										<Field>
											<FieldLabel
												htmlFor="password"
												className="flex items-center justify-between"
											>
												Password
												<Link
													to="/forgot-password"
													className="text-primary hover:underline"
												>
													Forgot password?
												</Link>
											</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<LockIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="password"
													type={showPassword ? "text" : "password"}
													placeholder={showPassword ? "•••••••••" : "Password"}
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													autoComplete="off"
													disabled={isPending}
													className="h-8"
												/>
												<InputGroupAddon align="inline-end" className="pr-2">
													<InputGroupButton
														type="button"
														variant="ghost"
														size="icon-sm"
														onClick={() => setShowPassword(!showPassword)}
													>
														<AnimatePresence initial={false} mode="popLayout">
															<motion.span
																key={showPassword ? "eye" : "eye-off"}
																initial={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
																animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
																exit={{ opacity: 0, scale: 0.25, filter: "blur(4px)" }}
																transition={{ type: "spring", duration: 0.3, bounce: 0 }}
															>
																{showPassword ? <EyeIcon /> : <EyeOffIcon />}
															</motion.span>
														</AnimatePresence>
													</InputGroupButton>
												</InputGroupAddon>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="flex flex-col gap-4">
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="sign-in"
								className="w-full"
								disabled={!isValid || !isDirty}
								loading={isSubmitting || isPending}
							>
								Sign in
							</Button>
						)}
					</form.Subscribe>
					<p className="text-sm text-muted-foreground">
						No account?{" "}
						<Link to="/sign-up" className="text-primary hover:underline">
							Sign up
						</Link>
					</p>
				</CardFooter>
			</Card>
		</Shell>
	);
}
